import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createOccurrence, fetchOccurrenceStatus, reissuePostCode, interpretAddress } from "../api/backend.js";
import Page from "../components/layout/Page.jsx";
import Summary from "../components/Summary.jsx";
import AttachmentPicker from "../components/AttachmentPicker.jsx";
import { attachmentsRequired, mergeResolutionSettings } from "../api/resolutions.js";
import { Alert, Button, Card, Field, Input, Select, Spinner } from "../components/ui.jsx";
import { useOccurrence } from "../context/OccurrenceContext.jsx";
import { useIframeHeight } from "../hooks/useIframeHeight.js";
import {
  approvalNotice,
  nextStepText,
  occurrenceErrorMessage,
  STATUS_TIMELINE,
  statusIndex,
  statusLabel,
  trackingUrl,
} from "../utils/occurrenceStatus.js";
import { qrSvg } from "../utils/qrSvg.js";
import { states } from "../utils/utils.js";
import { normalizeOrderName } from "../utils/session.js";
import styles from "./Post.module.css";

const emptyAddress = {
  fullName: "",
  street: "",
  number: "",
  complement: "",
  neighborhood: "",
  phone: "",
  zip: "",
  city: "",
  province: "",
};

const Post = () => {
  const { shop, name, email: emailParam } = useParams();
  const {
    store,
    occurrence,
    resolutionType,
    resolutionSettings,
    result,
    setResult,
    customerEmail,
    setCustomerEmail,
  } = useOccurrence();
  const email = decodeURIComponent(emailParam || customerEmail || occurrence?.email || occurrence?.customer_email || "");
  const [address, setAddress] = useState(emptyAddress);
  const [loadingAddress, setLoadingAddress] = useState(true);
  const [loadingConfirm, setLoadingConfirm] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState(false);
  const [copied, setCopied] = useState(false);
  const [attachments, setAttachments] = useState([]);
  const [reissuing, setReissuing] = useState(false);
  const [reissueError, setReissueError] = useState("");
  const portalSettings = mergeResolutionSettings(resolutionSettings);
  const attachmentMode = portalSettings.attachments.mode;
  const needAttachment = attachmentsRequired(portalSettings, occurrence?.products);
  const navigate = useNavigate();
  // O resultado salvo na sessão pode ser de outro pedido (consulta anterior).
  const baseOrder = (value) => normalizeOrderName(value).split("-")[0];
  const resultMatches = !result?.name || !name || baseOrder(result.name) === baseOrder(name);
  const confirmed = resultMatches && Boolean(result?.postReverse || result?.statusOccurrence || result?.id);
  const postReverse = result?.postReverse || {};
  const code = postReverse.postCode || result?.postCode;
  const zip = postReverse.zipDestination || postReverse.zip;
  const rawExpire = postReverse.expireDate || postReverse.expire;
  // "2026-11-01" -> "01/11/2026"
  const expire = /^\d{4}-\d{2}-\d{2}/.test(rawExpire || "")
    ? rawExpire.slice(0, 10).split("-").reverse().join("/")
    : rawExpire;
  // Código simulado (loja de teste) não existe nos Correios.
  const isMockCode = /^MK\d+BR$/.test(postReverse.postCode || "");
  const labelHref = postReverse.labelUrl || postReverse.label || postReverse.pdf;
  const trackHref = postReverse.trackingUrl || trackingUrl(code);
  const currentStatus = result?.statusOccurrence || "Aguardando postagem";
  // Loja configurada para liberar o código só depois de aprovar.
  const awaitingApproval = currentStatus === "Solicitado" && !code;
  const declined = currentStatus === "Recusado" || currentStatus === "Cancelado";
  const activeIndex = statusIndex(currentStatus);
  const approval = approvalNotice(result);
  const reverseFree = occurrence?.shipping_reverse_free || occurrence?.products?.some((item) => item.hasWarranty);
  const qrMarkup = code ? qrSvg(code) : "";
  const canReissue = Boolean(result?.canReissuePostCode);

  async function handleReissue() {
    setReissuing(true);
    setReissueError("");
    try {
      const updated = await reissuePostCode(shop, result?.name || name, email);
      if (updated) setResult(updated);
    } catch (err) {
      setReissueError(err?.response?.data?.message || "Não foi possível gerar um novo código agora.");
    } finally {
      setReissuing(false);
    }
  }

  useIframeHeight();

  useEffect(() => {
    if (email && email !== customerEmail) {
      setCustomerEmail(email);
    }
  }, [customerEmail, email, setCustomerEmail]);

  useEffect(() => {
    if (confirmed || occurrence?.products?.length) {
      return undefined;
    }
    if (!name || !email) {
      navigate(`/${shop}`);
      return undefined;
    }
    let cancelled = false;
    fetchOccurrenceStatus(shop, name, email)
      .then((existing) => {
        if (cancelled) {
          return;
        }
        if (existing) {
          setResult(existing);
          return;
        }
        navigate(`/${shop}`);
      })
      .catch(() => {
        if (!cancelled) {
          navigate(`/${shop}`);
        }
      });
    return () => {
      cancelled = true;
    };
  }, [confirmed, email, name, navigate, occurrence, setResult, shop]);

  useEffect(() => {
    if (confirmed) {
      setLoadingAddress(false);
      return undefined;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await interpretAddress({
          dataInterpreter: {
            shop,
            id: occurrence?.id,
            name: occurrence?.name,
            shipping_address: occurrence?.shipping_address,
          },
        });
        const shipping = data?.interpreter?.address?.shipping || {};
        if (!cancelled) {
          setAddress({
            fullName:
              occurrence?.shipping_address?.name ||
              [occurrence?.customer?.first_name, occurrence?.customer?.last_name].filter(Boolean).join(" "),
            street: shipping.street || occurrence?.shipping_address?.address1 || "",
            number: shipping.number || "",
            complement: shipping.complement || "",
            neighborhood: shipping.neighborhood || "",
            phone: occurrence?.shipping_address?.phone || "",
            zip: shipping.zip || occurrence?.shipping_address?.zip || "",
            city: shipping.city || occurrence?.shipping_address?.city || "",
            province: shipping.province || occurrence?.shipping_address?.province_code || "",
          });
        }
      } catch {
        if (!cancelled) {
          setAddress({
            ...emptyAddress,
            fullName:
              occurrence?.shipping_address?.name ||
              [occurrence?.customer?.first_name, occurrence?.customer?.last_name].filter(Boolean).join(" "),
            street: occurrence?.shipping_address?.address1 || "",
            zip: occurrence?.shipping_address?.zip || "",
            city: occurrence?.shipping_address?.city || "",
            province: occurrence?.shipping_address?.province_code || "",
            phone: occurrence?.shipping_address?.phone || "",
          });
        }
      } finally {
        if (!cancelled) {
          setLoadingAddress(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [confirmed, occurrence, shop]);

  // Status sempre fresco: a loja pode aprovar/recusar a qualquer momento (inclusive na Shopify).
  // Enquanto aguarda aprovação, atualiza a cada 30s para o código aparecer sem recarregar.
  const lookupName = baseOrder(name || result?.name || "");

  useEffect(() => {
    if (result && !resultMatches) setResult(undefined);
  }, [result, resultMatches, setResult]);
  useEffect(() => {
    if (!confirmed || !email || !lookupName) return undefined;
    let cancelled = false;
    const refresh = () =>
      fetchOccurrenceStatus(shop, lookupName, email)
        .then((fresh) => {
          if (!cancelled && fresh) setResult(fresh);
        })
        .catch(() => undefined);
    refresh();
    const timer = awaitingApproval ? setInterval(refresh, 30000) : null;
    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [confirmed, email, lookupName, shop, awaitingApproval]);

  const handleConfirm = async () => {
    if (!address.street || !address.zip || !address.city || !address.province) {
      setMessage("Preencha o endereço de coleta/postagem.");
      return;
    }
    if (!String(address.fullName || "").trim()) {
      setError(true);
      setMessage("Informe seu nome completo para a postagem nos Correios.");
      return;
    }
    if (needAttachment && attachments.length === 0) {
      setError(true);
      setMessage("Envie pelo menos uma foto do produto para continuar.");
      return;
    }
    setLoadingConfirm(true);
    setMessage("");
    setError(false);
    try {
      const payload = {
        ...occurrence,
        email,
        customer_email: email,
        customer: {
          ...(occurrence?.customer || {}),
          email,
        },
        resolutionType: occurrence?.resolutionType || resolutionType,
        attachments,
        shipping_address: {
          ...occurrence?.shipping_address,
          street: address.street,
          neighborhood: address.neighborhood,
          number: address.number,
          complement: address.complement,
          address1: `${address.street}, ${address.number}`.trim(),
          address2: `${address.complement}, ${address.neighborhood}`.trim(),
          city: address.city,
          zip: address.zip,
          province_code: address.province,
          phone: address.phone,
          name: String(address.fullName || "").trim(),
        },
      };
      const response = await createOccurrence(payload);
      const saved = {
        ...response,
        ...(response?.occurrence || {}),
        postReverse: response?.postReverse || response?.occurrence?.postReverse,
        name: response?.occurrence?.name || response?.name || occurrence?.name || name,
        statusOccurrence:
          response?.occurrence?.statusOccurrence || response?.statusOccurrence || response?.status || "Aguardando postagem",
      };
      if (response?.duplicated) {
        setResult(saved);
        setMessage(response.message || "Já existe uma solicitação para este pedido.");
        setError(!(response.postReverse || response.statusOccurrence));
        return;
      }
      setResult(saved);
    } catch (err) {
      setError(true);
      setMessage(occurrenceErrorMessage(err));
    } finally {
      setLoadingConfirm(false);
    }
  };

  const handleCopy = async () => {
    if (!code) {
      return;
    }
    try {
      await navigator.clipboard.writeText(code);
    } catch {
      window.parent.postMessage({ type: "copy", text: code }, "*");
    }
    window.parent.postMessage({ type: "copy", text: code }, "*");
    setCopied(true);
  };

  if (loadingAddress && !confirmed) {
    return (
      <Page title="Revisão" step="review" onBack={() => navigate(-1)}>
        <Card><Spinner label="Preparando o resumo..." /></Card>
      </Page>
    );
  }

  if (confirmed && !error) {
    return (
      <Page
        aside={
          <div className={styles.sideCard}>
            {awaitingApproval ? (
              <div className={styles.instructions}>
                <h3>O que acontece agora</h3>
                <ol>
                  <li>A loja confere a solicitação{attachments.length ? " e as fotos" : ""}.</li>
                  <li>Se aprovar, você recebe por e-mail o código de postagem dos Correios.</li>
                  <li>Leve o produto embalado à agência com o código. Não paga frete.</li>
                </ol>
              </div>
            ) : null}
            {!awaitingApproval && !declined ? (
          <div className={styles.instructions}>
            <h3>Como postar</h3>
            {reverseFree || code ? (
              <ol>
                <li>Leve o produto até uma agência dos Correios.</li>
                <li>Mostre o código ou o QR no balcão — sem imprimir etiqueta.</li>
                <li>Guarde o comprovante. A loja acompanha o rastreio automaticamente.</li>
              </ol>
            ) : (
              <ol>
                <li>Embalar o produto e levar até os Correios.</li>
                <li>Enviar para {store?.name || "a loja"} no endereço abaixo.</li>
              </ol>
            )}
            {!reverseFree && store?.store_return_address ? (
              <address className={styles.address}>
                {store.store_return_address.address}, {store.store_return_address.number}<br />
                {store.store_return_address.neighborhood} · CEP {store.store_return_address.zip_code}<br />
                {store.store_return_address.city} / {store.store_return_address.province_code}
              </address>
            ) : null}
          </div>
            ) : null}
            {customerEmail ? <p className={styles.hint}>Enviamos atualizações para {customerEmail} quando houver novidade.</p> : null}
          </div>
        }
        title="Acompanhe sua solicitação" step="status" onBack={() => { setResult(undefined); navigate(`/${shop}`); }} backLabel="Nova consulta">
        <Card className={styles.done}>
          <div className={styles.hero}>
            <h2>{awaitingApproval ? "Solicitação enviada" : declined ? "Solicitação encerrada" : "Solicitação confirmada"}</h2>
            <p>{statusLabel(currentStatus)}{name ? ` · Pedido #${name}` : ""}</p>
            {!declined && nextStepText(result) ? <p className={styles.nextStep}><strong>Agora:</strong> {nextStepText(result)}</p> : null}
          </div>
          {awaitingApproval ? (
            <Alert tone="info">
              <strong>Agora é com a loja.</strong> Vamos analisar o seu pedido e, quando aprovarmos, você recebe por e-mail o
              código de postagem dos Correios. Ele também aparece aqui.
            </Alert>
          ) : null}
          {declined ? (
            <Alert>
              <strong>{currentStatus === "Recusado" ? "A loja não aprovou esta solicitação." : "Esta solicitação foi cancelada."}</strong>{" "}
              {result?.declineNote || "Se tiver dúvidas, fale com o atendimento da loja."}
            </Alert>
          ) : null}
          {approval ? (
            <Alert tone="success">
              <strong>{approval.title}.</strong> {approval.text}
            </Alert>
          ) : null}
          <ol className={styles.timeline} aria-label="Status da solicitação">
            {STATUS_TIMELINE.map((step, index) => (
              <li
                key={step.id}
                className={index < activeIndex || currentStatus === "Concluído" ? styles.doneStep : index === activeIndex ? styles.currentStep : styles.todoStep}
                aria-current={index === activeIndex ? "step" : undefined}
              >
                <span>{index < activeIndex || currentStatus === "Concluído" ? "✓ " : ""}{step.label}</span>
              </li>
            ))}
          </ol>
          {code ? (
            <div className={styles.codeBox}>
              <p>Autorização de postagem dos Correios</p>
              <div className={styles.qrWrap}>
                <div className={styles.qr} dangerouslySetInnerHTML={{ __html: qrMarkup }} />
                <div>
                  <strong className={styles.code}>{code}</strong>
                  {zip ? <p>CEP destino: {zip}</p> : null}
                  {expire ? <p>Válido até {expire}</p> : null}
                </div>
              </div>
              <p className={styles.noPrint}>Dá para postar na agência só com este código. Não precisa imprimir etiqueta.</p>
              <div className={styles.actions}>
                <Button type="button" onClick={handleCopy}>{copied ? "Copiado" : "Copiar código"}</Button>
                {trackHref && !isMockCode ? (
                  <a className={styles.link} href={trackHref} target="_blank" rel="noreferrer">Rastrear nos Correios</a>
                ) : null}
                {labelHref ? (
                  <a className={styles.link} href={labelHref} target="_blank" rel="noreferrer">Abrir etiqueta (PDF), se quiser</a>
                ) : null}
              </div>
            </div>
          ) : null}
          {canReissue ? (
            <div className={styles.noPrint}>
              <Alert tone="info">
                <strong>{code ? "Seu código venceu?" : "Ainda sem código de postagem?"}</strong>{" "}
                Gere um novo código para postar nos Correios. O anterior deixa de valer.
              </Alert>
              <div className={styles.actions}>
                <Button type="button" onClick={handleReissue} disabled={reissuing}>
                  {reissuing ? "Gerando..." : "Gerar novo código"}
                </Button>
              </div>
              {reissueError ? <Alert tone="error">{reissueError}</Alert> : null}
            </div>
          ) : null}
        </Card>
      </Page>
    );
  }

  const reviewAside = (
    <>
      <Summary
        products={occurrence?.products}
        resolutionType={occurrence?.resolutionType || resolutionType}
        settings={resolutionSettings}
      />
      <div className={styles.confirmBox}>
        <p className={styles.hint}>
          {portalSettings.postCode.timing === "after_approval"
            ? "A loja analisa a solicitação antes de liberar o código de postagem. Você recebe por e-mail."
            : "Ao confirmar, você já recebe o código de postagem dos Correios."}
        </p>
        {message ? <Alert tone={error ? "error" : "info"}>{message}</Alert> : null}
        <Button onClick={handleConfirm} loading={loadingConfirm}>
          Confirmar solicitação
        </Button>
      </div>
    </>
  );

  return (
    <Page
      aside={reviewAside}
      title="Revise e confirme"
      subtitle="Confira os itens, a resolução e o endereço antes de gerar a autorização dos Correios."
      step="review"
      onBack={() => navigate(-1)}
    >
      <div className={styles.stack}>
        <Card className={styles.form}>
          <h3>Endereço de postagem</h3>
          <div className={styles.row}>
            <Field id="fullName" label="Nome completo">
              <Input id="fullName" autoComplete="name" value={address.fullName} onChange={(event) => setAddress({ ...address, fullName: event.target.value })} />
            </Field>
            <Field id="phone" label="Celular">
              <Input id="phone" type="tel" autoComplete="tel" value={address.phone} onChange={(event) => setAddress({ ...address, phone: event.target.value })} />
            </Field>
          </div>
          <Field id="street" label="Rua">
            <Input id="street" value={address.street} onChange={(event) => setAddress({ ...address, street: event.target.value })} />
          </Field>
          <div className={styles.row}>
            <Field id="number" label="Número">
              <Input id="number" value={address.number} onChange={(event) => setAddress({ ...address, number: event.target.value })} />
            </Field>
            <Field id="complement" label="Complemento">
              <Input id="complement" value={address.complement} onChange={(event) => setAddress({ ...address, complement: event.target.value })} />
            </Field>
          </div>
          <div className={styles.row}>
            <Field id="neighborhood" label="Bairro">
              <Input id="neighborhood" value={address.neighborhood} onChange={(event) => setAddress({ ...address, neighborhood: event.target.value })} />
            </Field>
            <Field id="zip" label="CEP">
              <Input id="zip" value={address.zip} onChange={(event) => setAddress({ ...address, zip: event.target.value })} />
            </Field>
          </div>
          <div className={styles.row}>
            <Field id="city" label="Cidade">
              <Input id="city" value={address.city} onChange={(event) => setAddress({ ...address, city: event.target.value })} />
            </Field>
            <Field id="province" label="UF">
              <Select id="province" value={address.province} onChange={(event) => setAddress({ ...address, province: event.target.value })}>
                {states.map((state) => (
                  <option key={state.value || "empty"} value={state.value} disabled={state.disabled}>{state.label}</option>
                ))}
              </Select>
            </Field>
          </div>
        </Card>
        {attachmentMode !== "off" ? (
          <AttachmentPicker
            required={needAttachment}
            defectOnly={attachmentMode === "required_for_defect"}
            maxFiles={portalSettings.attachments.maxFiles}
            value={attachments}
            onChange={(next) => {
              setAttachments(next);
              if (next.length) {
                setMessage("");
                setError(false);
              }
            }}
          />
        ) : null}
      </div>
    </Page>
  );
};

export default Post;
